import { exhaustiveFail } from '@cbridge-utils/error';
import {
  IApiServer, IApiServerCreate, IApiServerDetails, ApiServerType, IApiPricingClient, IApiTradingClient,
  IApiTradingConnections, IApiServerStream, IApiCreateServerStreamReq, IApiUpdateServerStreamReq, ApiConnectionStatus,
  IApiCTraderManagerSettingsDetails,
} from '@cbridge-packages/api';
import { ClientTranslator, ITradingClientSettings } from '@cbridge-packages/client';
import {
  IServer, ServerType, IServerDetails, IServerBase, IPricingClient, ITradingClient,
  IServerStream, IServerStreamForm, IServerStreamSettings, ConnectionStatus,
  ICTraderManagerSettings, ICTraderManagerSettingsDetails, ICTraderManagerSettingsForm,
} from './server.interface';
import { isMtServerType } from './_helpers/isMtServerType';
import { isRiskReportingSupported, usesCTraderManagerSettings } from './_helpers/isRiskReportingSupported';

export namespace ServerTranslator {
  export function fromApiServerType(type: ApiServerType): ServerType {
    switch (type) {
      case ApiServerType.MT4:
        return ServerType.MT4;
      case ApiServerType.MT5:
        return ServerType.MT5;
      case ApiServerType.cTrader:
        return ServerType.cTrader;
      case ApiServerType.FixTaker:
        return ServerType.FixTaker;
      default:
        return exhaustiveFail(type);
    }
  }

  export function toApiServerType(type: ServerType): ApiServerType {
    switch (type) {
      case ServerType.MT4:
        return ApiServerType.MT4;
      case ServerType.MT5:
        return ApiServerType.MT5;
      case ServerType.cTrader:
        return ApiServerType.cTrader;
      case ServerType.FixTaker:
        return ApiServerType.FixTaker;
      default:
        return exhaustiveFail(type);
    }
  }

  export function fromApiConnectionStatus(status: ApiConnectionStatus): ConnectionStatus {
    switch (status) {
      case ApiConnectionStatus.DISABLED:
        return ConnectionStatus.Disabled;
      case ApiConnectionStatus.OFFLINE:
        return ConnectionStatus.Offline;
      case ApiConnectionStatus.ONLINE:
        return ConnectionStatus.Online;
      case ApiConnectionStatus.NA:
        return ConnectionStatus.Na;
      case ApiConnectionStatus.UNKNOWN:
        return ConnectionStatus.Unknown;
      default:
        return exhaustiveFail(status);
    }
  }

  const getConnectionsLength = (list: IApiTradingConnections[keyof IApiTradingConnections]) => {
    return list?.length ?? 0;
  };

  export const fromApi = (server: IApiServer): IServer => {
    const { online, offline, disabled } = server.tradingConnections ?? {};

    return {
      serverId: server.id,
      serverName: server.name,
      nodeId: server.node?.id,
      nodeName: server.node?.name,
      type: ServerTranslator.fromApiServerType(server.type),
      streamsCount: getConnectionsLength(online) + getConnectionsLength(offline) + getConnectionsLength(disabled),
      clientSymbolCount: server.clientSymbolsCount,
      autosync: server.autosync,
      connectionStatus: fromApiConnectionStatus(server.connectionStatus),
      tradingConnections: server.tradingConnections,
      // Trading-related statuses may be omitted for a tenant without basicBridge (LB-5266), reporting without riskManagement
      pricingConnectionStatus: server.pricingConnectionStatus ? fromApiConnectionStatus(server.pricingConnectionStatus) : undefined,
      reportingConnectionStatus: server.reportingConnections ? fromApiConnectionStatus(server.reportingConnections) : undefined,
      throttlingInterval: server.throttlingInterval,
    };
  };

  export function toApi(params: Partial<Omit<IServerBase, 'cTraderManagerSettings'>> & { cTraderManagerSettings?: ICTraderManagerSettingsForm }): IApiServerCreate {
    return {
      name: params.serverName,
      type: ServerTranslator.toApiServerType(params.type),
      throttlingInterval: params.throttlingInterval,
      // Values left behind by hidden controls must not reach another type: the switch is MT4/MT5/cTrader only (LB-4701, LB-4732),
      // the cTrader Manager settings are rejected by BE for other types
      riskReportingEnabled: isRiskReportingSupported(params.type) ? params.riskReportingEnabled : undefined,
      cTraderManagerSettings: usesCTraderManagerSettings(params.type, params.riskReportingEnabled) && params.cTraderManagerSettings
        ? toCTraderManagerSettings(params.cTraderManagerSettings)
        : undefined,
    };
  }

  /**
   * Picks the four manager fields: a details object carries BE-derived host/port that neither the form nor a request
   * keeps, a form object is complete once the schema has passed. The API write shape is the same four fields.
   */
  export function toCTraderManagerSettings(settings: ICTraderManagerSettingsForm): ICTraderManagerSettings {
    return {
      plant: settings.plant,
      environment: settings.environment,
      managerLogin: settings.managerLogin,
      managerPassword: settings.managerPassword,
    };
  }

  export function fromApiCTraderManagerSettings(settings: IApiCTraderManagerSettingsDetails): ICTraderManagerSettingsDetails {
    return {
      plant: settings.plant,
      environment: settings.environment,
      managerLogin: settings.managerLogin,
      managerPassword: settings.managerPassword,
      host: settings.host,
      port: settings.port,
    };
  }

  export const fromApiServerStream = (serverId: IServer['serverId'], serverType: ServerType) =>
    (apiStream: IApiServerStream): IServerStream => {
      return {
        serverId,
        streamId: apiStream.id,
        streamName: apiStream.name,
        tradingEnabled: apiStream.tradingEnabled,
        pricingEnabled: apiStream.pricingEnabled,
        clientSymbolsCount: apiStream.clientSymbolsCount,
        settings: isMtServerType(serverType)
          // TODO(LB-3359): drop legacy flat-fields fallback once BE always returns `settings` for MT4/MT5
          ? apiStream.settings ?? {
            tradingGroups: apiStream.tradingGroups,
            securities: apiStream.securities,
            symbols: apiStream.symbols,
            useSymbolsConjunction: apiStream.useSymbolsConjunction,
            rejectClientRequestIfOffline: apiStream.rejectClientRequestIfOffline,
          }
          : undefined,
      };
    };

  export function toApiServerStreamCreate(form: IServerStreamForm): IApiCreateServerStreamReq {
    return {
      serverId: form.serverId,
      streamId: form.streamId,
      tradingEnabled: form.tradingEnabled,
      pricingEnabled: form.pricingEnabled,
      settings: form.settings ? toApiServerStreamSettings(form.settings) : undefined,
    };
  }

  export function toApiServerStreamUpdate(form: IServerStreamForm): IApiUpdateServerStreamReq {
    return {
      serverId: form.serverId,
      streamId: form.streamId,
      tradingEnabled: form.tradingEnabled,
      pricingEnabled: form.pricingEnabled,
      settings: form.settings ? toApiServerStreamSettings(form.settings) : undefined,
    };
  }

  function toApiServerStreamSettings(settings: IServerStreamSettings) {
    return {
      tradingGroups: settings.tradingGroups,
      useSymbolsConjunction: settings.useSymbolsConjunction ?? false,
      rejectClientRequestIfOffline: settings.rejectClientRequestIfOffline ?? false,
      securities: settings.securities,
      symbols: settings.symbols,
    };
  }

  export function toServerStreamForm(stream: IServerStream, defaultSettings: IServerStreamSettings): IServerStreamForm {
    return {
      serverId: stream.serverId,
      streamId: stream.streamId,
      tradingEnabled: stream.tradingEnabled,
      pricingEnabled: stream.pricingEnabled,
      settings: stream.settings ?? defaultSettings,
    };
  }

  export function fromTradingClientSettings(settings: ITradingClientSettings): IServerStreamSettings {
    return {
      tradingGroups: settings.groups,
      securities: settings.securities,
      symbols: settings.symbols,
      useSymbolsConjunction: settings.useSymbolsConjunction,
      rejectClientRequestIfOffline: settings.rejectClientRequestIfOffline,
    };
  }

  export const fromApiDetails = (serverId: IServer['serverId'], server: IApiServerDetails): IServerDetails => {
    const type = ServerTranslator.fromApiServerType(server.type);
    return {
      serverId,
      serverName: server.name,
      nodeId: server.node?.id,
      nodeName: server.node?.name,
      type,
      pricingClient: server.pricingClient
        ? ServerTranslator.fromApiPricingClient(serverId)(server.pricingClient)
        : undefined,
      tradingClients: (server.tradingClients ?? []).map(
        ServerTranslator.fromApiTradingClient(serverId, server.node?.id),
      ),
      streams: (server.streams ?? []).map(ServerTranslator.fromApiServerStream(serverId, type)),
      settings: server.settings,
      defaultSettings: {
        pricingSession: ClientTranslator.fromApiPricingSettings(server.defaultSettings?.pricingSession),
        tradingSession: ClientTranslator.fromApiTradingSettings(server.defaultSettings?.tradingSession),
      },
      pricingConnectionStatus: server.pricingClient?.status ? fromApiConnectionStatus(server.pricingClient?.status) : undefined,
      throttlingInterval: server.throttlingInterval,
      riskReportingEnabled: server.riskReportingEnabled ?? false,
      cTraderManagerSettings: server.cTraderManagerSettings
        ? fromApiCTraderManagerSettings(server.cTraderManagerSettings)
        : undefined,
    };
  };

  export const fromApiPricingClient = (serverId: IServer['serverId']) =>
    (client: IApiPricingClient): IPricingClient => {
      return {
        serverId,
        clientId: client.clientId,
        clientSymbolsCount: client.clientSymbolsCount,
        enabled: client.enabled,
        settings: ClientTranslator.fromApiPricingSettings(client.settings),
      };
    };

  export const fromApiTradingClient = (serverId: IServer['serverId'], nodeId: IServer['nodeId']) =>
    (client: IApiTradingClient): ITradingClient => {
      return {
        serverId,
        nodeId,
        clientId: client.clientId,
        clientSymbolsCount: client.clientSymbolsCount,
        enabled: client.enabled,
        status: fromApiConnectionStatus(client.status),
      };
    };
}
